import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Users,
  Briefcase,
  Calendar,
  UserPlus,
  Award,
  UserCheck,
  GraduationCap,
  UserCheck2,
  TrendingUp,
} from 'lucide-react';
import { Link } from 'react-router-dom';

// Import hooks
import { useSharedData } from '@/hooks/useSharedData';
import { useDashboardStats } from '@/hooks/useDashboardStats';
import AgreementNotifications from '@/components/AgreementNotifications';
import CompensationNotifications from '@/components/CompensationNotifications';
import { useAuthStore } from '@/stores/authStore';

const HRDashboardExtra: React.FC = () => {
  const { hasPermission } = useAuthStore();
  const { loading: sharedLoading, refreshAllData } = useSharedData();
  const stats = useDashboardStats();

  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [, setIsRefreshing] = useState(false);

  // Handle manual refresh - useCallback to keep stable reference
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refreshAllData();
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps: refreshAllData is a Zustand action - stable via getState()

  // Auto-refresh every 2 minutes - empty deps so interval is created ONLY once
  useEffect(() => {
    const interval = setInterval(() => {
      handleRefresh();
    }, 2 * 60 * 1000);

    return () => clearInterval(interval);
  }, []); // ← Empty array: DO NOT add refreshAllData here (causes infinite loop)

  // Set initial last updated time
  useEffect(() => {
    if (!lastUpdated && !sharedLoading) {
      setLastUpdated(new Date());
    }
  }, [sharedLoading, lastUpdated]);

  if (sharedLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header Skeleton */}
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>

          {/* Stats Cards Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {[...Array(5)].map((_, i) => (
              <Card key={i} className="animate-pulse border-0 shadow-lg bg-white/80 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="max-w-7xl mx-auto space-y-8">

        {/* Header Section */}


        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Employees Card */}
          {hasPermission('EMPLOYEES_VIEW') && (
            <Card className="h-full group hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-white/80 backdrop-blur-sm hover:bg-white/90">
              <CardContent className="p-6 h-full flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg group-hover:scale-110 transition-transform">
                    <Users className="h-6 w-6" />
                  </div>
                  <Badge variant="default" className="bg-blue-50 text-blue-700 hover:bg-blue-50 whitespace-nowrap border-0 ring-1 ring-inset ring-blue-700/10">
                    <span className="text-xs font-semibold">{stats.employees.total} {stats.employees.total === 1 ? 'Employee' : 'Employees'}</span>
                  </Badge>
                </div>
                <div className="space-y-2">
                  <div className="text-3xl font-bold text-gray-900">{stats.employees.total}</div>
                  <div className="text-sm text-gray-600 font-medium">Total Employees</div>
                </div>
                <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                  <TrendingUp className="h-4 w-4 text-green-500" />
                  <span>+{stats.employees.newThisMonth} this month</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Candidates Card */}
          {hasPermission('CANDIDATE_VIEW') && (
            <Card className="h-full group hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-white/80 backdrop-blur-sm hover:bg-white/90">
              <CardContent className="p-6 h-full flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg group-hover:scale-110 transition-transform">
                    <UserCheck className="h-6 w-6" />
                  </div>
                  <Badge variant="default" className="bg-green-50 text-green-700 hover:bg-green-50 whitespace-nowrap border-0 ring-1 ring-inset ring-green-700/10">
                    <span className="text-xs font-semibold">{stats.candidates.interview} {stats.candidates.interview === 1 ? 'Interview' : 'Interviews'}</span>
                  </Badge>
                </div>
                <div className="space-y-2">
                  <div className="text-3xl font-bold text-gray-900">{stats.candidates.total}</div>
                  <div className="text-sm text-gray-600 font-medium">Total Candidates</div>
                </div>
                <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                  <UserPlus className="h-4 w-4 text-green-500" />
                  <span>{stats.candidates.new} new</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Jobs Card */}
          {hasPermission('JOB_VIEW') && (
            <Card className="h-full group hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-white/80 backdrop-blur-sm hover:bg-white/90">
              <CardContent className="p-6 h-full flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-lg group-hover:scale-110 transition-transform">
                    <Briefcase className="h-6 w-6" />
                  </div>
                  <Badge variant="default" className="bg-purple-50 text-purple-700 hover:bg-purple-50 whitespace-nowrap border-0 ring-1 ring-inset ring-purple-700/10">
                    <span className="text-xs font-semibold">{stats.jobs.active} {stats.jobs.active === 1 ? 'Active Job' : 'Active Jobs'}</span>
                  </Badge>
                </div>
                <div className="space-y-2">
                  <div className="text-3xl font-bold text-gray-900">{stats.jobs.total}</div>
                  <div className="text-sm text-gray-600 font-medium">Total Jobs</div>
                </div>
                <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                  <Briefcase className="h-4 w-4 text-purple-500" />
                  <span>{stats.jobs.closed} closed</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Removed Interviews Card */}

          {/* Onboarding Card */}
          {hasPermission('ONBOARDING_VIEW') && (
            <Card className="h-full group hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-white/80 backdrop-blur-sm hover:bg-white/90">
              <CardContent className="p-6 h-full flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 text-white shadow-lg group-hover:scale-110 transition-transform">
                    <GraduationCap className="h-6 w-6" />
                  </div>
                  <Badge variant="default" className="bg-indigo-50 text-indigo-700 hover:bg-indigo-50 whitespace-nowrap border-0 ring-1 ring-inset ring-indigo-700/10">
                    <span className="text-xs font-semibold">{stats.onboarding.inProgress} In Progress</span>
                  </Badge>
                </div>
                <div className="space-y-2">
                  <div className="text-3xl font-bold text-gray-900">{stats.onboarding.total}</div>
                  <div className="text-sm text-gray-600 font-medium">Total Onboarding</div>
                </div>
                <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                  <UserCheck2 className="h-4 w-4 text-indigo-500" />
                  <span>{stats.onboarding.completed} completed</span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        {/* Notifications Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <AgreementNotifications />
          <CompensationNotifications />
        </div>

        {/* Quick Actions */}
        <Card className="hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-white/80 backdrop-blur-sm">
          <CardHeader className="pb-6">
            <CardTitle className="flex items-center gap-3 text-xl font-bold">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-lg">
                <Award className="h-5 w-5" />
              </div>
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {hasPermission('EMPLOYEES_CREATE') && (
                <Link to="/employees" className="group">
                  <Button variant="outline" className="w-full h-24 flex-col gap-3 rounded-xl border-2 hover:border-blue-500 hover:bg-blue-50 transition-all duration-300 group-hover:shadow-lg">
                    <UserPlus className="h-6 w-6 text-blue-600" />
                    <span className="font-semibold">Add Employee</span>
                  </Button>
                </Link>
              )}
              {hasPermission('CANDIDATE_CREATE') && (
                <Link to="/hiring/candidates" className="group">
                  <Button variant="outline" className="w-full h-24 flex-col gap-3 rounded-xl border-2 hover:border-green-500 hover:bg-green-50 transition-all duration-300 group-hover:shadow-lg">
                    <Users className="h-6 w-6 text-green-600" />
                    <span className="font-semibold">Add Candidate</span>
                  </Button>
                </Link>
              )}
              {hasPermission('JOB_CREATE') && (
                <Link to="/hiring/jobs" className="group">
                  <Button variant="outline" className="w-full h-24 flex-col gap-3 rounded-xl border-2 hover:border-purple-500 hover:bg-purple-50 transition-all duration-300 group-hover:shadow-lg">
                    <Briefcase className="h-6 w-6 text-purple-600" />
                    <span className="font-semibold">Post Job</span>
                  </Button>
                </Link>
              )}
              {hasPermission('CANDIDATE_VIEW') && (
                <Link to="/hiring/candidates" className="group">
                  <Button variant="outline" className="w-full h-24 flex-col gap-3 rounded-xl border-2 hover:border-orange-500 hover:bg-orange-50 transition-all duration-300 group-hover:shadow-lg">
                    <Calendar className="h-6 w-6 text-orange-600" />
                    <span className="font-semibold">Manage Hiring Flow</span>
                  </Button>
                </Link>
              )}
              {hasPermission('ONBOARDING_VIEW') && (
                <Link to="/hiring/onboarding" className="group">
                  <Button variant="outline" className="w-full h-24 flex-col gap-3 rounded-xl border-2 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-300 group-hover:shadow-lg">
                    <GraduationCap className="h-6 w-6 text-indigo-600" />
                    <span className="font-semibold">Start Onboarding</span>
                  </Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HRDashboardExtra; 